import React, { useCallback, useMemo, useEffect, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as yup from 'yup'
import {
  faMemoCircleInfo,
  faUser,
  faAwardSimple,
  faList,
  faContainerStorage,
  faUser as partyIcon,
  faCashRegister as amountIcon,
  faLandmarkDome as governmentIcon,
  faArrowRightToBracket as submissionIcon,
  faPrint
} from '@fortawesome/pro-regular-svg-icons'
import DataTableSection from 'src/components/era-package/forms/DataTableSection'
import DataTableSectionWithUpload from 'src/components/era-package/forms/DataTableSectionWithUpload'
import {
  header,
  filer,
  importer,
  bond,
  miscellaneous,
  additional,
  manifest,
  containers,
  print,
  buildOptionsMap as headerOptions
} from 'src/views/abi/fields/entry-summary-header'
import {
  parties,
  buildPartyData
} from 'src/views/abi/fields/entry-summary-cargo'
import {
  alcohol,
  aluminum,
  items,
  skuTemplate,
  lineItem,
  lineItemFee,
  misc,
  softwood,
  steel,
  optionsMap as lineOptions
} from 'src/views/abi/fields/entry-summary-line'
import {
  entry,
  amount,
  government,
  submission,
  optionsMap as confOptions
} from 'src/views/abi/fields/entry-summary-confirmation'
import {
  addTabKeyToFields,
  addTabKeyToOptions,
  generateDefaultValues,
  stripTabKeyFromFields
} from 'src/utils/era-package/tab-utils'
import AbiEntrySummaryApiUrls from '../../../../../apiUrls/AbiEntrySummaryApiUrls'
import ImportManifestInfo from './ImportManifestInfo'

import sampleLineItems from 'src/views/abi/fields/line-items'

/**
 * Each hook should return an `Object` the following:
 * @param {String} name - tab identifier
 * @param {Array} formSections - Form sections and fields to display.
 *                  FIELD NAMES SHOULD FOLLOW `tabName.fieldName`
 * @param {Array} defaultValues - Default values to be fed into the form manager.
 *                  FIELD NAMES SHOULD FOLLOW `fieldName`
 * @param {Object} optionsMap - Options to use in the form
 *                  FIELD NAMES SHOULD FOLLOW `tabName.fieldName`
 * @param {Array?} field - OPTIONAL Flat array of all fields. This is used to generate the validation schema.
 *                  FIELD NAMES SHOULD FOLLOW `fieldName`
 * @param {(Object) => Yup} schema - OPTIONAL Validation schema. Takes in an object and expects a Yup object in return.
 */

export const useHeaderForm = (id) => {
  const key = 'header'
  const [headerOptionsMap, setHeaderOptionsMap] = useState({})

  useEffect(() => {
    const load = async () => {
      const resolvedOptionsMap = await headerOptions()
      const finalOptionsMap = addTabKeyToOptions(resolvedOptionsMap, key)

      setHeaderOptionsMap(finalOptionsMap)
    }

    load()
  }, [key])

  const defaultValues = () => {
    const initialData = {}

    // Initialize regular fields
    ;[
      ...header,
      ...filer,
      ...importer,
      ...miscellaneous,
      ...additional,
      ...print
    ].forEach((field) => {
      initialData[field.name] = field.defaultValue
    })

    // Initialize table data
    initialData.bond = []
    initialData.additional = []
    initialData.manifest = []
    initialData.containers = []

    return initialData
  }

  // Set up form sections including table sections
  const formSections = [
    {
      title: 'Header Information',
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />,
      fields: addTabKeyToFields(header, key)
    },
    {
      title: 'Filer',
      icon: <FontAwesomeIcon icon={faUser} />,
      fields: addTabKeyToFields(filer, key)
    },
    {
      title: 'Importer',
      icon: <FontAwesomeIcon icon={faUser} />,
      fields: addTabKeyToFields(importer, key)
    },
    {
      title: 'Bond Information',
      icon: <FontAwesomeIcon icon={faAwardSimple} />,
      type: 'table',
      name: `${key}.bond`,
      tableComponent: DataTableSection,
      tableFields: bond,
      maxRows: 2,
    },
    {
      title: 'Miscellaneous',
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />,
      fields: addTabKeyToFields(miscellaneous, key)
    },
    {
      title: 'Additional Information',
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />,
      type: 'table',
      name: `${key}.additional`,
      tableComponent: DataTableSection,
      tableFields: additional,
      maxRows: 5,
      condition: (formData) => formData?.[key].postSummaryCorrection === true
    },
    {
      title: 'Manifest',
      icon: <FontAwesomeIcon icon={faList} />,
      type: 'table',
      name: `${key}.manifest`,
      tableComponent: DataTableSection,
      tableFields: manifest,
      maxRows: 999
    },
    {
      title: 'Container Information',
      icon: <FontAwesomeIcon icon={faContainerStorage} />,
      type: 'table',
      name: `${key}.containers`,
      tableComponent: DataTableSection,
      tableFields: containers,
      maxRows: 99
    },
    {
      title: 'Print',
      icon: <FontAwesomeIcon icon={faPrint} />,
      fields: addTabKeyToFields(print, key)
    }
  ]

  return {
    name: key,
    formSections,
    defaultValues: defaultValues(),
    fields: [...header, ...filer, ...importer, ...miscellaneous],
    schema: (baseObj) =>
      yup.object({
        ...baseObj,
        additional: yup
          .array(
            yup.object({
              pscHeaderReasonCode: yup
                .string()
                .required()
                .label('PSC Header Reason Code')
            })
          )
          .when('postSummaryCorrection', {
            is: true,
            then: (fieldSchema) => fieldSchema.min(1)
          })
          .label('PSC Header Reason Code')
      }),
    optionsMap: addTabKeyToOptions(headerOptionsMap, key)
  }
}

export const useCargoEntityForm = (id) => {
  const key = 'cargo'
  const [partyFieldsState, setPartyFieldsState] = useState({})
  const [optionsMapState, setOptionsMapState] = useState({})

  useEffect(() => {
    let alive = true

    buildPartyData().then(({ partyFields, optionsMap }) => {
      if (!alive) return
      setPartyFieldsState(partyFields)
      setOptionsMapState(optionsMap)
    })

    return () => {
      alive = false
    }
  }, [id])

  // Set up form sections including table sections
  const formSections = useMemo(
    () =>
      Object.keys(parties).map((partyCode) => ({
        title: parties[partyCode],
        icon: <FontAwesomeIcon icon={partyIcon} />,
        fields: addTabKeyToFields(partyFieldsState[partyCode] ?? [], key)
      })),
    [partyFieldsState]
  )

  // Set up form data
  const allFields = useMemo(
    () => formSections.flatMap((section) => section.fields),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: generateDefaultValues(allFields, key),
    fields: stripTabKeyFromFields(allFields, key),
    optionsMap: addTabKeyToOptions(optionsMapState ?? {}, key)
  }
}

export const useLineItemForm = (id, lineData, setLineData) => {
  const key = 'line'
  const [filesUnderProcessing, setFilesUnderProcessing] = useState('')

  // Fetch files under processing on mount or when ID changes
  useEffect(() => {
    const fetchData = async () => {
      const filesProcessing = await fetchFilesUnderProcessing(id)
      setFilesUnderProcessing(filesProcessing)
    }
    fetchData()
  }, [id])

  const handleUploadSuccess = useCallback(() => {
    console.log('Custom post upload logic for entry summary line')
  }, [])

  const fetchLineItems = async (abiTransId) => {
    const response = await fetch(
      `${AbiEntrySummaryApiUrls.GET_LINE_ITEMS}?abiTransId=${abiTransId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      }
    )
    const json = await response.json()
    return json.data
  }

  const calculateLineItems = async (abiTransId) => {
    const response = await fetch(
      `${AbiEntrySummaryApiUrls.CALCULATE_LINE_ITEMS}?abiTransId=${abiTransId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      }
    )
    const json = await response.json()
    return json.data
  }

  const calculateAllDuty = async (abiTransId) => {
    console.log("calculating duty..")
    const response = await fetch(
      `${AbiEntrySummaryApiUrls.CALCULATE_ALL_DUTY_LINE_ITEMS}?abiTransId=${abiTransId}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      }
    )
    const json = await response.json()
    return json.data
  }

  const fetchFilesUnderProcessing = async (abiTransId) => {
    const response = await fetch(
      `${AbiEntrySummaryApiUrls.GET_FILE_UPLOAD}?abiTransId=${abiTransId}&status=P`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      }
    )

    const json = await response.json()
    const fileNames = Array.isArray(json.data)
      ? json.data
          .filter((file) => file.decFileName)
          .map((file) => file.decFileName)
          .join('\n')
      : ''

    return fileNames
  }

  const handleApiCall = useCallback(
    async (file, formData, formMethods) => {
      const saveManifestInfo = formData?.importManifestInfo ?? false
      const apiFormData = new FormData()
      apiFormData.append('file', file)
      apiFormData.append('abiTransId', id)
      apiFormData.append('saveManifestInfo', saveManifestInfo)

      try {
        const response = await fetch(AbiEntrySummaryApiUrls.UPLOAD_LINE_ITEMS, {
          method: 'POST',
          headers: {
            Authorization:
              'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          },
          body: apiFormData
        })

        if (!response.ok) {
          throw new Error('Error uploading file')
        }

        const result = await response.json()
        console.log('File uploaded successfully:', result)
        setFilesUnderProcessing(file.name)
      } catch (error) {
        throw new Error('Error uploading file')
      }
    },
    [id]
  )

  const handleRefresh = useCallback(async () => {
    try {
      const lineItems = await fetchLineItems(id)
      const filesProcessing = await fetchFilesUnderProcessing(id)
      setFilesUnderProcessing(filesProcessing)
      setLineData(lineItems)
      return lineItems
    } catch (error) {
      console.error('Error refreshing line items:', error)
    }
  }, [id, setLineData])

  const handleClearAll = useCallback(async () => {
    const formData = new FormData()
    formData.append('abiTransId', id)

    try {
      const response = await fetch(
        AbiEntrySummaryApiUrls.DELETE_ALL_LINE_ITEMS,
        {
          method: 'POST',
          headers: {
            Authorization:
              'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
          },
          body: formData
        }
      )

      if (!response.ok) {
        throw new Error('Error clearing all line items')
      }
      const result = await response.json()
      console.log('All line items cleared successfully:', result)
      return result.data
    } catch (error) {
      throw new Error('Error clearing all line items')
    }
  }, [id])

  const handleDeleteRow = useCallback(async (id) => {
    const formData = new FormData()
    formData.append('id', id)

    try {
      const response = await fetch(AbiEntrySummaryApiUrls.DELETE_LINE_ITEMS, {
        method: 'POST',
        headers: {
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: formData
      })

      if (!response.ok) {
        throw new Error('Error deleting file')
      }
      const result = await response.json()
      console.log('File deleted successfully:', result)
      return result.data
    } catch (error) {
      throw new Error('Error deleting file')
    }
  }, [])

  const handleCloneRow = useCallback(async (id) => {
    try {
      const response = await fetch(`${AbiEntrySummaryApiUrls.CLONE_LINE_ITEM}?lineItemId=${id}`, {
        method: 'GET',
        headers: {
          Authorization:
            'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        }
      })

      if (!response.ok) {
        throw new Error('Error cloning line item')
      }
      const result = await response.json()
      console.log('Line item cloned successfully:', result)
      return result.data
    } catch (error) {
      throw new Error('Error cloning line item')
    }
  }, [])

  const handleDownload = useCallback(async (abiTransId) => {
    const formData = new FormData()
    formData.append('abiTransId', abiTransId)

    const response = await fetch(AbiEntrySummaryApiUrls.DOWNLOAD_LINE_ITEMS, {
      method: 'POST',
      headers: {
        Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
      },
      body: formData
    })

    if (!response.ok) {
      const responseBody = await response.json() // Parse the JSON body
      throw new Error(responseBody.message || 'Error downloading file')
    }

    // Extract filename from headers
    const contentDisposition = response.headers.get('Content-Disposition')
    let fileName = 'ES_Line_Items_' + abiTransId + '.xlsx'
    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        fileName = decodeURIComponent(match[1])
      }
    }

    // Convert response to blob and trigger download
    const blob = await response.blob()
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }, [])

  const handleCalculate = useCallback(async () => {
    try {
      const lineItems = await calculateLineItems(id)
      setLineData(lineItems)
      return lineItems
    } catch (error) {
      console.error('Error refreshing line items:', error)
    }
  }, [])

  const handleCalculateAllDuty = useCallback(async () => {
    try {
      const lineItems = await calculateAllDuty(id)
      setLineData(lineItems)
      return lineItems
    } catch (error) {
      console.error('Error refreshing line items:', error)
    }
  }, [])

  const formSections = useMemo(
    () => [
      {
        title: 'Line Items',
        icon: <FontAwesomeIcon icon={faList} />,
        type: 'table',
        name: `${key}`,
        tableComponent: DataTableSectionWithUpload,
        disableEdit: false,
        tableFields: items,
        formFields: [
          ...skuTemplate,
          ...lineItem,
          ...lineItemFee,
          ...misc,
          ...softwood,
          ...steel,
          ...aluminum,
          ...alcohol
        ],
        onUploadSuccess: handleUploadSuccess,
        handleApiCall: handleApiCall,
        handleRefresh: handleRefresh,
        handleClearAll: handleClearAll,
        filesUnderProcessing: filesUnderProcessing,
        handleDeleteRow: handleDeleteRow,
        handleCloneRow: handleCloneRow,
        uploadFormComponent: <ImportManifestInfo />,
        allowDownload: true,
        handleDownload: handleDownload,
        useESLineEdit: true,
        handleCalculate: handleCalculate,
        handleCalculateAllDuty: handleCalculateAllDuty,
        enableCalculate: true,
      }
    ],
    [
      handleApiCall,
      handleUploadSuccess,
      handleRefresh,
      handleClearAll,
      filesUnderProcessing,
      handleDeleteRow,
      handleCloneRow,
      handleDownload
    ]
  )

  return {
    name: key,
    formSections,
    optionsMap: addTabKeyToOptions(lineOptions, key),
    schema: (baseObj) => {
      return yup.array().nullable().default([])
    }
  }
}

export const useConfirmationForm = (id) => {
  const key = 'confirmation'
  const formSections = [
    {
      title: 'Entry & Transportation',
      icon: <FontAwesomeIcon icon={faMemoCircleInfo} />,
      fields: addTabKeyToFields(entry, key),
      readOnly: true
    },
    {
      title: 'Amount & Duty',
      icon: <FontAwesomeIcon icon={amountIcon} />,
      fields: addTabKeyToFields(amount, key)
    },
    /**
    {
      title: 'Government Agency Requirements',
      icon: <FontAwesomeIcon icon={governmentIcon} />,
      fields: addTabKeyToFields(government, key),
      readOnly: true,
    },**/
    {
      title: 'Submission',
      icon: <FontAwesomeIcon icon={submissionIcon} />,
      fields: addTabKeyToFields(submission, key)
    }
  ]

  // Set up form data
  const allFields = useMemo(
    () => formSections.flatMap((section) => section.fields),
    [formSections] // Make sure to update the dependency array
  )

  return {
    name: key,
    formSections,
    defaultValues: generateDefaultValues(allFields, key),
    fields: stripTabKeyFromFields(allFields, key),
    optionsMap: addTabKeyToOptions(confOptions, key),
    schema: (baseObj) => {
      return yup.object({
        ...baseObj,
        epaRequired: yup.string().nullable(),
        epaEntered: yup.string().nullable(),
        fdaRequired: yup.string().nullable(),
        fdaEntered: yup.string().nullable()
      })
    }
  }
}
